import { useNavigate } from "react-router-dom";

import { ProblemDetailsError } from "../../../packages/fxid-openapi-client/src/ApiHelpers.ts";

export async function rerouteTo500OnError<T>(
	history: ReturnType<typeof useNavigate>,
	func: () => Promise<T>
): Promise<T | undefined> {
	return redirectOnError("/error", history, func);
}

export type ErrorResponseData = {
	status: number;
	statusText: string;
	body: string;
	url: string;
};

export async function redirectOnError<T>(
	to: string,
	history: ReturnType<typeof useNavigate>,
	func: () => Promise<T>
): Promise<T | undefined> {
	return new Promise<T | undefined>((resolve, reject) => {
		func()
			.then(resolve)
			.catch(async (e) => {
				log.error(e);
				let responseData: ErrorResponseData | undefined = undefined;
				if (e instanceof Response) {
					responseData = {
						status: e.status,
						statusText: e.statusText,
						body: await e.text(),
						url: e.url
					};
				}
				history(to, { state: responseData, replace: true });
				resolve(undefined);
			});
	});
}

/**
 * https://stackoverflow.com/a/58362609
 */
/**
 * Результат валидации email
 */
export interface EmailValidationResult {
	/** Валиден ли email */
	isValid: boolean;
	/** Код ошибки для локализации (null если валиден) */
	errorCode: "email_required" | "email_too_short" | "email_too_long" | "email_invalid_format" | "email_local_part_too_long" | "email_domain_too_long" | "email_consecutive_dots" | "email_dot_boundary" | null;
}

/**
 * Валидирует email адрес согласно RFC 5321.
 * Проверяет:
 * - Наличие значения
 * - Длину (от 5 до 254 символов)
 * - Формат с использованием улучшенного regex
 * - Длину локальной части (до @) - максимум 64 символа
 * - Длину доменной части (после @) - максимум 253 символа
 * - Отсутствие двойных точек
 * - Локальная часть не начинается/заканчивается точкой
 */
export const validateEmail = (value: string | null | undefined): EmailValidationResult => {
	if (!value) {
		return { isValid: false, errorCode: "email_required" };
	}

	const trimmed = value.trim();

	// Минимальная длина: a@b.c = 5 символов
	if (trimmed.length < 5) {
		return { isValid: false, errorCode: "email_too_short" };
	}

	// Максимальная длина по RFC 5321
	if (trimmed.length > 254) {
		return { isValid: false, errorCode: "email_too_long" };
	}

	// Улучшенный regex для email валидации
	// - Локальная часть: буквы, цифры, точки, дефисы, подчеркивания, плюсы и прочие допустимые символы
	// - Домен: буквы, цифры, дефисы, точки; минимум 2 символа для TLD
	const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

	if (!emailRegex.test(trimmed)) {
		return { isValid: false, errorCode: "email_invalid_format" };
	}

	const [localPart, domain] = trimmed.split("@");

	// RFC 5321: локальная часть не более 64 символов
	if (localPart.length > 64) {
		return { isValid: false, errorCode: "email_local_part_too_long" };
	}

	// RFC 5321: доменная часть не более 253 символов
	if (domain.length > 253) {
		return { isValid: false, errorCode: "email_domain_too_long" };
	}

	// Проверка на двойные точки в локальной части
	if (localPart.includes("..")) {
		return { isValid: false, errorCode: "email_consecutive_dots" };
	}

	// Проверка что локальная часть не начинается и не заканчивается точкой
	if (localPart.startsWith(".") || localPart.endsWith(".")) {
		return { isValid: false, errorCode: "email_dot_boundary" };
	}

	return { isValid: true, errorCode: null };
};

/**
 * Простая проверка валидности email (возвращает boolean)
 */
export const isValidEmail = (value: string | null | undefined): boolean => {
	return validateEmail(value).isValid;
};

/**
 * Тип кода ошибки валидации email
 */
export type EmailErrorCode = NonNullable<EmailValidationResult["errorCode"]>;

/**
 * Возвращает код ошибки валидации email (для использования с i18next)
 */
export const getEmailValidationErrorCode = (value: string | null | undefined): EmailErrorCode | null => {
	const result = validateEmail(value);
	return result.errorCode;
};

/**
 * https://stackoverflow.com/a/58362609
 */
export const exitFullscreen = async () => {
	try {
		// As correctly mentioned in the accepted answer, exitFullscreen only works on document
		const cancellFullScreen =
			document.exitFullscreen ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(document as any).mozCancelFullScreen ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(document as any).webkitExitFullscreen ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(document as any).msExitFullscreen;
		log.info("Exiting fullscreen");
		await cancellFullScreen.call(document);
		log.info("Exited fullscreen");
	} catch (err) {
		// Тут будет ошибка дескать "мы не вышли из фулскрина потому-что мы не там - нам не важно
		// ignore
	}
};
